import * as React from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import DashboardCard from '@/pages/dashboard/DashboardCard'
import { Skeleton } from '@/components/ui/skeleton'
import { buttonVariants } from '@/components/ui/button'
import useGetForecastScore from '@/services/api/cash-flow/useGetForecastScore'
import {
  monthlyBarGeometry,
  offPercent,
  forecastToneColor,
  forecastToneColorSoft,
} from '@/pages/dashboard/forecastScoreMath'
import CardErrorState from '@/pages/dashboard/widgets/CardErrorState'
import ForecastInsights from '@/pages/dashboard/widgets/ForecastInsights'

const TONE_CHIP_CLASS: Record<string, string> = {
  positive: 'bg-green-tr-12 text-txt-success',
  neutral: 'bg-accent-tr-12 text-txt-accent',
  caution: 'bg-yellow-tr-12 text-[hsl(40,100%,30%)]',
  negative: 'bg-red-tr-12 text-txt-error',
}

const BAR_HEIGHT_PX = 68
// Reserved above the tallest possible bar for its value label — the tallest
// bar in any window always reaches exactly BAR_HEIGHT_PX by construction
// (monthlyBarGeometry scales the max score in the window to that height), so
// this headroom is never actually a guess against "what if a bar is 100%".
const LABEL_HEADROOM_PX = 22

export default function ForecastScoreCard() {
  // 6 closed months (offsets -6..-1) plus the current one (offset 0, open or
  // provisional) — never averaged client-side, summary.average is server-computed.
  const { data, status, refetch } = useGetForecastScore({
    offset: 6,
    numberOfPeriods: 6,
    periodType: 'month',
    summary: true,
  })

  const summary = data?.summary
  const periods = data?.data ?? []

  const lastClosedPeriod = React.useMemo(
    () => periods.find((p) => p.offset === summary?.last_closed?.offset),
    [periods, summary],
  )

  // Exactly the periods that feed summary.average — never the current
  // (possibly provisional) one — so the bar chart and the average always
  // agree on what they're explaining.
  const barPoints = React.useMemo(
    () =>
      periods
        .filter((p) => p.offset < 0 && p.score !== null)
        .sort((a, b) => a.offset - b.offset)
        .map((p) => ({ offset: p.offset, score: p.score as number, tone: p.band?.tone, month: p.month })),
    [periods],
  )
  const { bars, averageLinePx } = React.useMemo(
    () => monthlyBarGeometry(barPoints, summary?.average, BAR_HEIGHT_PX),
    [barPoints, summary],
  )

  const dateRange = React.useMemo(() => {
    if (barPoints.length === 0) return null
    const first = periods.find((p) => p.offset === barPoints[0].offset)
    const last = periods.find((p) => p.offset === barPoints[barPoints.length - 1].offset)
    if (!first || !last) return null
    return `${format(new Date(first.end), 'MMM')} – ${format(new Date(last.end), 'MMM yyyy')}`
  }, [barPoints, periods])

  const currentPeriod = React.useMemo(
    () => periods.find((p) => p.offset === 0) ?? periods[periods.length - 1],
    [periods],
  )

  const isEmpty = status === 'success' && (!summary || summary.average === null)
  const chipClass = TONE_CHIP_CLASS[summary?.band?.tone ?? 'neutral'] ?? TONE_CHIP_CLASS.neutral

  const shareIn = lastClosedPeriod?.components?.share_in
  const shareOut = shareIn !== undefined && shareIn !== null ? 1 - shareIn : undefined

  return (
    <DashboardCard
      title={
        <div>
          <div className="text-[14.5px] font-semibold text-txt-primary">Forecast score</div>
          {dateRange && (
            <div className="mt-0.5 text-xs text-txt-secondary">Six-month average · {dateRange}</div>
          )}
        </div>
      }
      headerRight={
        status === 'success' &&
        !isEmpty &&
        summary?.band && <span className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-xs font-bold ${chipClass}`}>{summary.band.label}</span>
      }
    >
      {status === 'pending' && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-20 w-full" />
          <div className="text-[11px] text-txt-secondary">
            Loading — each card resolves on its own
          </div>
        </div>
      )}

      {status === 'error' && <CardErrorState onRetry={() => refetch()} />}

      {status === 'success' && isEmpty && (
        <div className="flex h-24 flex-col items-center justify-center gap-1.5 text-center text-sm text-txt-secondary">
          <div className="font-medium text-txt-primary">No closed month yet</div>
          <div className="text-xs">
            Your first score appears once{' '}
            {currentPeriod ? format(new Date(currentPeriod.end), 'MMMM') : 'the current period'} closes.
          </div>
          <Link to="/table" className={buttonVariants({ variant: 'outline', size: 'sm', className: 'mt-1' })}>
            Add estimates
          </Link>
        </div>
      )}

      {status === 'success' && !isEmpty && summary && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-4">
            <div className="flex flex-col gap-1">
              <div className="font-raleway text-[42px] font-bold leading-none text-txt-primary">
                {summary.average}
              </div>
              {lastClosedPeriod && lastClosedPeriod.score !== null && (
                <div className="text-xs text-txt-secondary">
                  {format(new Date(lastClosedPeriod.end), 'MMMM')} closed at{' '}
                  <span className="font-bold text-txt-primary">{lastClosedPeriod.score}</span>
                </div>
              )}
            </div>

            {bars.length >= 2 && (
              <div className="flex flex-col gap-1">
                {/* Value labels + bars only, in their own fixed-height row —
                    keeping month labels out of this box guarantees the
                    tallest bar's label (headroom above BAR_HEIGHT_PX) never
                    overlaps the card edge, regardless of how tall any bar gets. */}
                <div
                  className="relative flex items-end gap-2"
                  style={{ height: BAR_HEIGHT_PX + LABEL_HEADROOM_PX }}
                >
                  {averageLinePx !== null && (
                    <div
                      className="absolute left-0 right-0 border-t border-dashed border-[hsl(215,19%,65%)]"
                      style={{ bottom: averageLinePx }}
                    />
                  )}
                  {bars.map((bar) => (
                    <div key={bar.offset} className="flex flex-1 flex-col items-center gap-1">
                      <div
                        className="text-[10.5px] font-bold"
                        style={{ color: forecastToneColor(bar.tone) }}
                      >
                        {bar.score}
                      </div>
                      <div
                        className="w-full rounded-t"
                        style={{ height: bar.heightPx, backgroundColor: forecastToneColorSoft(bar.tone) }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  {bars.map((bar, i) => (
                    <div
                      key={bar.offset}
                      className={`flex-1 text-center text-[10.5px] ${i === bars.length - 1 ? 'font-bold text-txt-primary' : 'text-txt-secondary'}`}
                    >
                      {bar.month}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <ForecastInsights />

          {/* Turn 5 moves this breakdown into "Where you came up short" — it
              stays here until that card (FE 21) is built, rather than dropping
              the only explanation of the last close. */}
          {lastClosedPeriod?.components && (
            <div className="flex flex-col gap-1.5 border-t border-light pt-2.5">
              <div className="text-[11.5px] font-semibold text-txt-primary">
                How {format(new Date(lastClosedPeriod.end), 'MMMM')} reached {lastClosedPeriod.score}
              </div>
              {shareOut !== undefined && (
                <ComponentRow
                  label="Money out"
                  share={shareOut}
                  off={offPercent(lastClosedPeriod.components.out_error)}
                  score={lastClosedPeriod.components.out_score}
                />
              )}
              {shareIn !== undefined && (
                <ComponentRow
                  label="Money in"
                  share={shareIn}
                  off={offPercent(lastClosedPeriod.components.in_error)}
                  score={lastClosedPeriod.components.in_score}
                />
              )}
            </div>
          )}
        </div>
      )}
    </DashboardCard>
  )
}

function ComponentRow({
  label,
  share,
  off,
  score,
}: {
  label: string
  share: number
  off: string
  score: number | undefined
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-[72px] shrink-0 text-[11.5px] text-txt-secondary">{label}</div>
      <div className="h-2 min-w-0 flex-1 overflow-hidden rounded bg-gray-low">
        <div
          className="h-full bg-accent-default"
          style={{ width: `${Math.round(share * 100)}%` }}
        />
      </div>
      <div className="shrink-0 whitespace-nowrap text-[11.5px] text-txt-primary">
        {off} off {score !== undefined && <>→ <span className="font-bold">{score}</span></>}
      </div>
    </div>
  )
}
